import React, { useState } from 'react';
import { ChevronLeft, LayoutGrid } from 'lucide-react';

interface NavItem {
    id: string;
    label: string;
    icon: React.ElementType;
}

interface MiniAppShellProps {
    title: string;
    icon: React.ElementType;
    navItems: NavItem[];
    children: (activeTab: string, setActiveTab: (id: string) => void) => React.ReactNode;
    onBack: () => void;
}

export const MiniAppShell = ({ title, icon: Icon, navItems, children, onBack }: MiniAppShellProps) => {
    const [activeTab, setActiveTab] = useState(navItems[0]?.id);

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <div className="fixed inset-0 bg-slate-50 z-50 flex overflow-hidden animate-in fade-in slide-in-from-right-4 duration-300">
            {/* Sidebar Navigation - hidden on mobile */}
            <div className="hidden lg:flex lg:w-64 bg-white border-r border-slate-200 flex-col">
                <div className="p-6 border-b border-slate-100 flex items-center gap-3">
                    <div className="p-2 bg-slate-900 rounded-xl text-white">
                        <Icon size={20} />
                    </div>
                    <div className="overflow-hidden">
                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight truncate">{title}</h3>
                        <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">Mini-App Portal</p>
                    </div>
                </div>

                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                                activeTab === item.id
                                    ? 'bg-slate-900 text-white shadow-lg shadow-slate-200'
                                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                            }`}
                        >
                            <item.icon size={18} />
                            {item.label}
                        </button>
                    ))}
                </nav>

                <div className="p-4 border-t border-slate-100">
                    <button
                        onClick={onBack}
                        className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition-all text-xs font-bold uppercase tracking-widest"
                    >
                        <ChevronLeft size={14} /> Back to Hub
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
            )}

            {/* Mobile Sidebar Drawer */}
            <div className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-200 transform transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-900 rounded-xl text-white">
                            <Icon size={20} />
                        </div>
                        <div className="overflow-hidden">
                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight truncate">{title}</h3>
                            <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">Mini-App Portal</p>
                        </div>
                    </div>
                    <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-slate-400 hover:text-slate-600">
                        <ChevronLeft size={18} />
                    </button>
                </div>
                <nav className="p-4 space-y-2">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                                activeTab === item.id
                                    ? 'bg-slate-900 text-white shadow-lg shadow-slate-200'
                                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                            }`}
                        >
                            <item.icon size={18} />
                            {item.label}
                        </button>
                    ))}
                </nav>
                <div className="p-4 border-t border-slate-100 absolute bottom-0 left-0 right-0">
                    <button
                        onClick={onBack}
                        className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition-all text-xs font-bold uppercase tracking-widest"
                    >
                        <ChevronLeft size={14} /> Back to Hub
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <header className="h-16 bg-white border-b border-slate-200 px-4 lg:px-8 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                        <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-2 -ml-2 text-slate-500 hover:text-slate-800">
                            <LayoutGrid size={18} />
                        </button>
                        <span className="text-slate-300 font-medium text-xs shrink-0">Modules</span>
                        <ChevronRight size={12} className="text-slate-300 shrink-0" />
                        <span className="text-slate-800 font-bold text-xs uppercase tracking-tight truncate">
                            {navItems.find(i => i.id === activeTab)?.label}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <div className="px-3 py-1 bg-slate-100 rounded-full text-[10px] font-black text-slate-500 uppercase tracking-widest">
                            Active Session
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-4 lg:p-8 pb-20 lg:pb-8 bg-slate-50">
                    <div className="max-w-7xl mx-auto">
                        {children(activeTab, setActiveTab)}
                    </div>
                </main>

                {/* Mobile Bottom Tab Bar */}
                <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30 flex items-center justify-around px-2 py-1 safe-area-inset-bottom">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-all ${
                                activeTab === item.id
                                    ? 'text-slate-900'
                                    : 'text-slate-400'
                            }`}
                        >
                            <item.icon size={20} />
                            <span className={`text-[9px] font-bold uppercase tracking-widest ${activeTab === item.id ? 'text-slate-900' : 'text-slate-400'}`}>
                                {item.label.split(' ')[0]}
                            </span>
                        </button>
                    ))}
                </nav>
            </div>
        </div>
    );
};

// Helper for the breadcrumb chevron
const ChevronRight = ({ size, className }: { size: number, className: string }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
);
